import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedButton from './AnimatedButton';
import TechIcon from './TechIcon';
import { ALLIANCE_PRESENTS_UPPER, BRAND_PRODUCT } from '../constants/brand';

const Hero = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [result, setResult] = useState(null);

  const handleVerify = (e) => {
    e.preventDefault();
    const match = inputCode.match(/ISSGRE-049-(\d{3,})/);
    if (inputCode.includes('ISSGRE-049') && match) {
      const num = parseInt(match[1], 10);
      if (num > 40) {
        setResult({ valid: true, message: '¡Certificado válido!' });
        return;
      }
    }
    setResult({ valid: false, message: 'Certificado no válido o código incorrecto.' });
  };

  const methodologySteps = [
    {
      step: 1,
      title: 'Entendemos tu Negocio',
      tag: 'Análisis',
      icon: 'analysis',
    },
    {
      step: 2,
      title: 'Lo Estandarizamos',
      tag: 'Procesos',
      icon: 'standardization',
    },
    {
      step: 3,
      title: 'Lo Automatizamos',
      tag: 'Tecnología',
      icon: 'automation',
    },
  ];

  const stats = [
    { label: '41+ Proyectos', icon: 'projects' },
    { label: '95% Satisfacción', icon: 'star' },
    { label: '10+ Años', icon: 'clock' },
  ];

  return (
    <div className="hero-section min-h-screen relative overflow-x-hidden">
      <div className="absolute inset-0">
        <img
          src="/images/negocio_inteligente.jpg"
          alt="Transformación Digital"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-darkBlue-900"></div>
      </div>

      <div className="relative flex flex-col items-center justify-center min-h-screen text-white text-center px-4 sm:px-6 lg:px-8">
        <div className="bg-darkBlue-800 rounded-corporate-xl p-5 sm:p-7 md:p-9 lg:p-12 border border-primary-700 shadow-corporate-2xl max-w-5xl mx-auto">
          <div className="text-center">
            <header className="mb-8 sm:mb-10 text-left rounded-lg sm:rounded-xl p-6 sm:p-8 md:p-10 bg-primary-950/50 backdrop-blur-sm border border-primary-600/40 shadow-lg">
              <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-accent-400 mb-3 sm:mb-4 font-display">
                {ALLIANCE_PRESENTS_UPPER}
              </p>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white font-display tracking-tight leading-none mb-4 sm:mb-5">
                {BRAND_PRODUCT}
              </h2>
              <p className="text-readable-base sm:text-readable-lg text-primary-100/90 leading-relaxed max-w-2xl">
                Implementación de <span className="text-accent-400 font-semibold">ERP y tecnologías empresariales</span> a medida.
              </p>
            </header>

            <div className="mb-8 sm:mb-10 max-w-4xl mx-auto">
              <p className="text-readable-sm sm:text-readable-base font-semibold uppercase tracking-wider text-accent-400 mb-4 sm:mb-6 font-display">
                Nuestra metodología en 3 pasos
              </p>
              <ol className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
                {methodologySteps.map((item, index) => (
                  <li key={item.step} className="relative">
                    {index > 0 && (
                      <span
                        className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 text-accent-400/80 text-xl"
                        aria-hidden
                      >
                        →
                      </span>
                    )}
                    <div className="h-full rounded-xl border border-primary-600/50 bg-primary-900/40 p-5 text-center">
                      <div className="flex items-center justify-center gap-3 mb-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-darkBlue-900 font-bold text-sm font-display">
                          {item.step}
                        </span>
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-800 border border-primary-600">
                          <TechIcon name={item.icon} className="w-5 h-5 text-accent-400" />
                        </div>
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary-300 mb-1">{item.tag}</p>
                      <h3 className="text-readable-base sm:text-readable-lg font-bold text-white font-display leading-snug">
                        {item.title}
                      </h3>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="text-readable-base text-primary-100/80 mt-5 sm:mt-6 font-body max-w-2xl mx-auto leading-relaxed">
                Un proceso claro: conocemos tu operación, la ordenamos y la llevamos a software.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 md:gap-6 w-full max-w-2xl mx-auto mt-6 sm:mt-8 px-4">
            <AnimatedButton
              as="button"
              onClick={() => navigate('/servicios')}
              variant="secondary"
              size="large"
              className="py-4 sm:py-5 px-6 sm:px-10 rounded-lg font-display tracking-wide text-center w-full sm:min-w-[200px] touch-manipulation"
              style={{ minHeight: '56px' }}
            >
              <span className="flex items-center justify-center gap-2">
                <span className="font-semibold">Explorar Servicios</span>
                <TechIcon name="arrow-right" className="w-5 h-5" />
              </span>
            </AnimatedButton>

            <AnimatedButton
              as="button"
              onClick={() => navigate('/servicios')}
              variant="outline"
              size="large"
              className="py-4 sm:py-5 px-6 sm:px-10 rounded-lg font-display tracking-wide text-center w-full sm:min-w-[200px] touch-manipulation border-white text-white hover:bg-white hover:text-darkBlue-900"
              style={{ minHeight: '56px' }}
            >
              <span className="flex items-center justify-center gap-2">
                <TechIcon name="rocket" className="w-5 h-5" />
                <span className="font-semibold">Iniciar Proyecto</span>
              </span>
            </AnimatedButton>
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-10 mt-12 text-gray-200 text-readable-base">
            {stats.map((item) => (
              <div key={item.label} className="flex items-center gap-2.5">
                <TechIcon name={item.icon} className="w-6 h-6 text-success-400 shrink-0" />
                <span className="font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 md:p-8 max-w-md w-full relative">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 p-1"
              onClick={() => {
                setShowModal(false);
                setInputCode('');
                setResult(null);
              }}
              aria-label="Cerrar"
            >
              <TechIcon name="close" className="w-7 h-7" />
            </button>
            <h3 className="text-readable-xl font-bold mb-4 text-primary font-display">Validar Certificado</h3>
            <form onSubmit={handleVerify} className="space-y-4">
              <input
                type="text"
                placeholder="Ingrese el código de certificado"
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary text-readable-base text-black font-body"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                required
              />
              <button
                type="submit"
                className="w-full bg-primary text-white py-3 px-4 rounded-md hover:bg-primary-700 transition-colors duration-300 font-semibold font-display text-readable-base"
              >
                Verificar
              </button>
            </form>
            {result && (
              <div className={`mt-4 p-3 rounded-md text-center font-bold text-readable-base ${result.valid ? 'bg-green-100 text-green-700 border border-green-400' : 'bg-red-100 text-red-700 border border-red-400'} font-body`}>
                {result.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
